import { Component, Input, inject } from '@angular/core';

import { SlipState } from '@bpos/v1/my-bets';
import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import {
    IncrementalPayoutDetails,
    PromoTokenPlaceBetSignPostingInfo,
} from 'packages/sports/common/betslip/modules/reward-tokens/reward-tokens.model';

import { MyBetsBetSlip, PayoutType } from '../../../my-bets-base/models/my-bets-viewmodels';
import { RewardTokenType, SignPostingOfferType } from '../../../tokens-base/token-base.models';
import { TokensCalculatorService } from '../../../tokens-base/tokens-calculator.service';
import { TokensSummaryService } from '../../../tokens-base/tokens-summary.service';
import { MyBetsUtilService } from '../../services/my-bets-util.service';

@Component({ template: '' })
export abstract class MyBetsBaseUsedRewardTokenComponent {
    @Input() betslip: MyBetsBetSlip;

    protected sitecore = inject(Sitecore);
    protected moneyService = inject(MoneyService);
    protected tokensSummaryService = inject(TokensSummaryService);
    private tokensCalculator = inject(TokensCalculatorService);
    protected myBetsUtilService = inject(MyBetsUtilService);

    abstract get detailsText(): string;
    abstract getRiskFreeRewardTemplate(isIncremental: boolean): string;

    protected getRiskFreePayoutDetails(): string {
        const payoutType = this.betslip.usedRewardToken?.payoutType;
        const riskFreePercentage = this.betslip.usedRewardToken?.additionalInformation?.riskFreePercentage;
        const riskFreeMaxCompensation = this.betslip.usedRewardToken?.additionalInformation?.riskFreeMaxCompensation;

        if (!payoutType || !riskFreePercentage || !riskFreeMaxCompensation) {
            return '';
        }

        if (this.betslip.state === SlipState.Won && !this.betslip.isCachedOut) {
            return this.myBetsUtilService.isBetCardRedesigned()
                ? this.sitecore.crmTokens.linearRewardTokens.MyBetsRiskFreeWon
                : this.sitecore.crmTokens.myBets.RiskFreeUsedNoRewardDescription;
        }

        const payoutAmount = this.tokensCalculator.getTokenPercentageReturns(riskFreeMaxCompensation, riskFreePercentage, this.betslip.stake.value);
        const incrementalDetails = this.getIncrementalDetails(payoutType, SignPostingOfferType.RiskFree);
        const displayPayoutType = incrementalDetails.slabAmount
            ? this.sitecore.crmTokens.riskFree.betslipSummary.IncrementalPayoutFreebet
            : this.tokensSummaryService.getDisplayPayoutType(payoutType, RewardTokenType.RiskFreeBet);
        const rewardTemplate = this.getRiskFreeRewardTemplate(!!incrementalDetails.slabAmount);

        return interpolateDoubleBracesString(rewardTemplate, {
            payoutType: displayPayoutType,
            amount: this.moneyService.transform(payoutAmount, true),
            noOfSlabs: incrementalDetails.noOfSlabs ?? 1,
            payout: this.moneyService.transform(incrementalDetails.slabAmount, true),
        });
    }

    protected getRewardTargetDescription(rewardTargetType?: string): string | undefined {
        return rewardTargetType === RewardTargetType.WelcomeOffer ? this.sitecore.crmTokens.myBets.WelcomeOfferUsed : undefined;
    }

    private getIncrementalDetails(payoutType: PayoutType, signPostOfferType: SignPostingOfferType): IncrementalPayoutDetails {
        if (payoutType !== PayoutType.FreeBet) {
            return {};
        }

        if (this.betslip.signPostingRewards.length > 0) {
            // when we have promo signpostings along with token specific signpost then need to filter w.r.t token signPostOfferType
            const signPostDetails = this.betslip.signPostingRewards.find((signPost) => signPost.offerType === signPostOfferType.toString());

            return {
                noOfSlabs: signPostDetails?.noOfSlabs,
                slabAmount: signPostDetails?.slabAmount,
            };
        } else {
            const placeBetSignPost = (this.betslip.placeBetSignPosting ?? {}) as PromoTokenPlaceBetSignPostingInfo;

            return {
                noOfSlabs: placeBetSignPost.noOfSlabs,
                slabAmount: placeBetSignPost.slabAmount,
            };
        }
    }
}
